/** Насколько далеко утянули шторку и как быстро отпустили. */
export type SheetGesture = {
  /** Сдвиг вниз в пикселях; вверх — отрицательный. */
  dy: number;
  /** Сколько длился жест, мс. */
  elapsedMs: number;
  /** Высота самой шторки — порог считается от неё, а не от экрана. */
  height: number;
};

/** Ниже этого сдвига жест — не перетаскивание, а промах пальцем по ручке. */
export const SHEET_SLOP_PX = 8;

/** Доля высоты шторки, после которой отпускание закрывает её. */
const DISMISS_FRACTION = 0.3;

/** Скорость, при которой закрываем независимо от пройденного пути, px/мс. */
const FLICK_VELOCITY = 0.5;

/**
 * Закрывать ли шторку после того, как палец отпустили.
 *
 * Два признака, любой из них достаточен. Расстояние отвечает медленному осознанному движению:
 * утянул треть высоты — значит решил закрыть. Скорость отвечает короткому рывку, которым шторки
 * закрывают в жизни: без него жест ощущается тугим, потому что рывок пальцем редко проходит
 * треть экрана, а намерение в нём читается однозначно.
 *
 * Порог берётся от высоты шторки, а не от высоты экрана: одна и та же треть у подтверждения на
 * 200px и у формы на весь экран — это совершенно разные движения пальцем, и общий порог в
 * пикселях сделал бы низкую шторку неотрывной, а высокую — закрывающейся от неосторожного касания.
 */
export const shouldDismissSheet = ({ dy, elapsedMs, height }: SheetGesture): boolean => {
  if (dy < SHEET_SLOP_PX) {
    return false;
  }
  if (height > 0 && dy >= height * DISMISS_FRACTION) {
    return true;
  }
  // Мгновенный жест дал бы деление на ноль, а «бесконечная скорость» здесь — это как раз рывок.
  const velocity = elapsedMs > 0 ? dy / elapsedMs : Number.POSITIVE_INFINITY;
  return velocity >= FLICK_VELOCITY;
};

/**
 * Насколько сдвинуть шторку за пальцем.
 *
 * Вверх она не едет: над ней ничего нет, и «резинка» в пустоту читалась бы как сломанная
 * анимация. Ноль вместо отрицательного сдвига оставляет шторку на месте — палец скользит по
 * ручке, ничего не происходит, и это честно описывает, что вверх тянуть некуда.
 */
export const sheetOffset = (dy: number): number => Math.max(dy, 0);
